import { demoConfig } from '../config/demoConfig';

interface TokenResponse {
  skinToken: string | null;
  error?: string;
}

/**
 * Generates a random demo player username from the non-reserved pool
 * Players 1..reservedCount are preserved; picks from reservedCount+1..demoPlayerCount
 */
export const getRandomDemoUsername = (): string => {
  const min = demoConfig.demoPlayerReservedCount + 1;
  const max = demoConfig.demoPlayerCount;
  const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
  return `${demoConfig.demoPlayerPrefix}${randomNum}`;
};

/**
 * Fetches a token for the given username and skinId from the backend API
 * @param username - The username to get a token for
 * @param skinId - The skin ID
 * @returns Promise with the token response
 */
export const fetchToken = async (
  username: string,
  skinId: string
): Promise<TokenResponse> => {
  const apiUrl = `${demoConfig.backendApi}/gettoken`;

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ username, skinId }),
      mode: 'cors',
    });

    console.log('Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(
        `Failed to fetch token: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching token:', error);
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('Detailed error:', errorMessage);
    return {
      skinToken: null,
      error: errorMessage,
    };
  }
};

/**
 * Constructs the lobby URL with authentication parameters
 * @param username - The username
 * @param skinToken - The authentication token
 * @param skinId - The skin ID
 * @param baseUrl - The base URL of the poker client
 * @returns The complete lobby URL with query parameters
 */
export const constructLobbyUrl = (
  username: string,
  skinToken: string,
  skinId: string,
  baseUrl: string
): string => {
  // Use lobby.php which handles mobile/desktop routing and passes parameters
  const url = new URL(baseUrl);
  url.searchParams.set('username', username);
  url.searchParams.set('skinToken', skinToken);
  url.searchParams.set('skinId', skinId);
  return url.toString();
};

/**
 * Opens the poker client with auto-login
 * lobby.php will automatically handle desktop/mobile routing based on device
 * @param baseUrl - The base URL of the poker client (default: desktop from config)
 * @param skinId - The skin ID (default: from config)
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const launchPokerWithAutoLogin = async (
  baseUrl: string = demoConfig.pokerDesktop,
  skinId: string = demoConfig.skinId
): Promise<boolean> => {
  // IMPORTANT: Open window IMMEDIATELY (synchronously) to prevent mobile Safari popup blocking
  // Mobile Safari blocks popups opened after async operations
  const newWindow = window.open(baseUrl, '_blank');

  if (!newWindow) {
    console.warn('Popup blocked - unable to open window');
    return false;
  }

  try {
    // Get a random demo username
    const username = getRandomDemoUsername();

    console.log(
      `Attempting auto-login with username: ${username}, skinId: ${skinId}`
    );

    // Fetch token from backend
    const tokenResponse = await fetchToken(username, skinId);

    if (!tokenResponse.skinToken) {
      console.error('Failed to get token:', tokenResponse.error);
      alert(`Failed to get token: ${tokenResponse.error}`);
      // Fallback: redirect to base URL without token
      newWindow.location.href = baseUrl;
      return false;
    }

    // Construct the lobby URL with authentication
    // lobby.php will handle device detection and routing
    const lobbyUrl = constructLobbyUrl(
      username,
      tokenResponse.skinToken,
      skinId,
      baseUrl
    );

    console.log('Opening poker client with auto-login:', lobbyUrl);

    // Redirect the already-open window to the lobby URL
    newWindow.location.href = lobbyUrl;

    return true;
  } catch (error) {
    console.error('Error launching poker client with auto-login:', error);
    alert(`Error launching poker client with auto-login: ${error}`);
    // Fallback: redirect to base URL without token
    if (newWindow && !newWindow.closed) {
      newWindow.location.href = baseUrl;
    }
    return false;
  }
};
